// features/subscription/SubscriptionSuccess.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { FaCheckCircle, FaSpinner } from 'react-icons/fa';

const SubscriptionSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError(t('subscription.noSessionId'));
      setLoading(false);
      return;
    }

    // Use a ref to track if verification has been attempted
    // This persists between renders and prevents duplicate API calls
    if (!verificationAttempted.current) {
      const verifySession = async () => {
        try {
          setLoading(true);
          // Set the ref to true immediately to prevent concurrent calls
          verificationAttempted.current = true;
          console.log('Verifying subscription session:', sessionId);
          
          const result = await api.verifySubscriptionCheckout(sessionId);
          setVerificationResult(result);
          
          // Refresh subscription data in context only once
          await refreshSubscription();
        } catch (err) {
          console.error('Error verifying subscription:', err);
          setError(err.message || t('subscription.verificationError'));
        } finally {
          setLoading(false);
        }
      };

      verifySession();
    }
  }, [searchParams, refreshSubscription, t]); // Remove verified from dependencies

  const handleNavigateToDashboard = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <Card className="w-full max-w-md mx-auto mt-10">
        <CardHeader className="text-center">
          <CardTitle>{t('subscription.processingPayment')}</CardTitle>
          <CardDescription>{t('subscription.pleaseWait')}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <FaSpinner className="animate-spin text-primary text-4xl" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto mt-10">
        <CardHeader className="text-center">
          <CardTitle className="text-red-600">{t('subscription.paymentError')}</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-center">
          <Button onClick={handleNavigateToDashboard}>
            {t('subscription.returnToDashboard')}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto mt-10">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <FaCheckCircle className="text-green-500 text-5xl" />
        </div>
        <CardTitle>{t('subscription.paymentSuccessful')}</CardTitle>
        <CardDescription>
          {verificationResult?.subscription 
            ? t('subscription.subscriptionActivated', { 
                plan: verificationResult.subscription.plan_name,
                price: parseFloat(verificationResult.subscription.price || 0).toFixed(2)
              })
            : t('subscription.paymentProcessed')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {verificationResult?.subscription && (
          <div className="text-center">
            <p className="mb-2">{t('subscription.validUntil', { 
              date: new Date(verificationResult.subscription.end_date).toLocaleDateString() 
            })}</p>
            <p>{t('subscription.creditsAdded', { 
              credits: verificationResult.subscription.credits || 0 
            })}</p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button onClick={handleNavigateToDashboard}>
          {t('subscription.viewSubscription')}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SubscriptionSuccess;
